

import { useContext, useState } from "react";

import { GlobalContext } from "../../Context/GlobalContext";

import { toast } from "react-toastify";

const API = {
  // LIST: "/Inventory/v1.0/getInventoryItems",

  LIST: "/Inventory/v2.0/getInventoryItems",

  ITEM_DETAILS: "/Inventory/v1.0/getInventoryItemDetails",

  CATEGORIES: "/Inventory/v1.0/getInventoryCategories",

  SUBCATEGORIES: "/Inventory/v1.0/getInventorySubCategories",

  BULK_BARCODE: "/Inventory/updateItemBarcode",

  UPDATE_CATALOG: "/Order/v1.0/updateCatalogItems",

  RECIPE_INGREDIENTS: "/Inventory/v1.0/getRecipeIngredient",

  GET_FOOD_CUSTOMIZATIONS: "/Inventory/getFoodCustomizations",

  INSERT_FOOD_CUSTOMIZATIONS: "/Inventory/insertFoodCustomizations",
};

export default function useInventory() {
  const { api, anlookApi, userID } = useContext(GlobalContext);

  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);

  const [bulkItems, setBulkItems] = useState([]);

  const [bulkLoading, setBulkLoading] = useState(false);

  const [hasNextPage, setHasNextPage] = useState(false);

  const [inventoryPage, setInventoryPage] = useState(1);

  const INVENTORY_LIMIT = 50;

  const call = (path, payload) => api.post(path, payload);

  // ---------------- INVENTORY LIST ----------------

  const fetchInventory = async (search = "", page = 1) => {
    try {
      setLoading(true);

      const limit = INVENTORY_LIMIT;

      const safePage = Number(page) > 0 ? Number(page) : 1;

      const offset = (safePage - 1) * limit;

      const res = await call(API.LIST, {
        userid: userID,

        searchText: String(search || "").trim(),

        platform: "",

        limit: String(limit),

        offset: String(offset),
      });

      if (res?.data?.responseCode === "00") {
        const pageItems = Array.isArray(res?.data?.response)
          ? res.data.response
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

        setItems(pageItems);

        setInventoryPage(safePage);

        setHasNextPage(pageItems.length === limit);

        return pageItems;
      }

      setItems([]);

      setHasNextPage(false);

      return [];
    } catch (err) {
      console.error("Fetch Inventory Error:", err);

      setItems([]);

      setHasNextPage(false);

      return [];
    } finally {
      setLoading(false);
    }
  };

  // ---------------- ITEM DETAILS ----------------

  const getItemDetails = async (itemId) => {
    try {
      const res = await call(API.ITEM_DETAILS, {
        userid: userID,

        itemId: String(itemId),
      });

      if (res?.data?.responseCode === "00") {
        return res.data;
      }

      return null;
    } catch (err) {
      console.error(" API Error:", err);

      return null;
    }
  };

  // ---------------- CATEGORIES ----------------

  const getCategories = async () => {
    try {
      const res = await call(API.CATEGORIES, { userid: userID });

      if (res?.data?.responseCode === "00") {
        return res.data.response || [];
      }

      return [];
    } catch (err) {
      console.error(err);

      return [];
    }
  };

  // ---------------- SUB CATEGORIES ----------------

  const getSubCategories = async (category) => {
    try {
      const res = await call(API.SUBCATEGORIES, {
        userid: userID,

        category,
      });

      if (res?.data?.responseCode === "00") {
        return res.data.response || [];
      }

      return [];
    } catch (err) {
      console.error(err);

      return [];
    }
  };

  // ---------------- BULK BARCODE ----------------

  const updateAllBarcodes = async () => {
    try {
      const res = await api.post(API.BULK_BARCODE, { usCode: String(userID) });

      return res.data;
    } catch (err) {
      console.error(err);

      return null;
    }
  };

  const addBarcodeToItem = async ({ barCode, inventoryId }) => {
    try {
      const res = await call("/Inventory/v1.0/Addbarcodetoitem", {
        userid: userID,

        barCode,

        inventoryId,
      });

      return res.data;
    } catch (err) {
      console.error(err);
    }
  };

  // ---------------- UPLOAD ITEM IMAGE ----------------

  const updateInventoryItem = async (payload) => {
    try {
      const res = await call("/Inventory/editCatalogItems", {
        usCode: userID,

        ...payload,
      });

      return res.data;
    } catch (err) {
      console.error(err);

      return null;
    }
  };

  // ---------------- UPLOAD ITEM IMAGE ----------------

  const uploadItemImage = async ({ item_name, image_data, type = "IMG" }) => {
    try {
      if (!anlookApi) {
        throw new Error("Anlook API instance missing");
      }

      if (!userID) {
        throw new Error("User ID missing");
      }

      if (!image_data) {
        throw new Error("Image data missing");
      }

      const res = await anlookApi.post("/v1/uploadItemImagev2.php", {
        userid: String(userID),

        item_name: item_name || "inventory_item",

        type,

        image_data,
      });

      if (String(res?.data?.status) === "0" && res?.data?.image_url) {
        return res.data;
      }

      throw new Error(
        res?.data?.message ||
          res?.data?.responseMessage ||
          "Image upload failed",
      );
    } catch (error) {
      console.error("uploadItemImage error:", error?.response?.data || error);

      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.responseMessage ||
          error?.message ||
          "Image upload failed",
      );

      return null;
    }
  };

  const updateCatalogItem = async (payload) => {
    try {
      const finalPayload = {
        ...payload,

        categoryId: "",
      };

      const res = await call(API.UPDATE_CATALOG, finalPayload);

      return res.data;
    } catch (err) {
      console.error(err);

      return null;
    }
  };

  // ---------------- FOOD CUSTOMIZATIONS ----------------

  const getFoodCustomizations = async () => {
    try {
      const res = await call(API.GET_FOOD_CUSTOMIZATIONS, {
        userId: String(userID),
      });

      if (res?.data?.responseCode !== "00") {
        return [];
      }

      const rows = Array.isArray(res?.data?.response) ? res.data.response : [];

      return rows

        .map((item) => {
          let parsedOptions = [];

          if (Array.isArray(item?.options)) {
            parsedOptions = item.options;
          } else {
            try {
              const parsed = JSON.parse(item?.options_json || "[]");

              parsedOptions = Array.isArray(parsed) ? parsed : [];
            } catch {
              parsedOptions = [];
            }
          }

          return {
            pk_customization_id: item?.pk_customization_id,

            user_id: item?.user_id,

            customize_field: String(item?.customize_field || "").trim(),

            options_json: item?.options_json || "[]",

            options: parsedOptions.map((option) => String(option)),
          };
        })

        .filter((item) => item.customize_field);
    } catch (err) {
      console.error("Get Food Customizations Error:", err);

      return [];
    }
  };

  const insertFoodCustomizations = async ({ customizeField, options }) => {
    try {
      const cleanField = String(customizeField || "").trim();

      const cleanOptions = Array.isArray(options)
        ? options.map((option) => String(option).trim()).filter(Boolean)
        : [];

      if (!cleanField) {
        toast.error("Customization field is required");

        return null;
      }

      if (!cleanOptions.length) {
        toast.error("At least one option is required");

        return null;
      }

      const res = await call(API.INSERT_FOOD_CUSTOMIZATIONS, {
        userId: String(userID),

        customizeField: cleanField,

        options: cleanOptions,
      });

      if (res?.data?.responseCode === "00") {
        toast.success(
          res?.data?.responseMessage || "Customization added successfully",
        );

        return res.data;
      }

      toast.error(res?.data?.responseMessage || "Failed to add customization");

      return null;
    } catch (err) {
      console.error("Insert Food Customizations Error:", err);

      toast.error(
        err?.response?.data?.responseMessage ||
          err?.message ||
          "Failed to add customization",
      );

      return null;
    }
  };

  // ---------------- RECIPE INGREDIENTS ----------------

  const getRecipeIngredients = async (itemId) => {
    try {
      const res = await call(API.RECIPE_INGREDIENTS, {
        userid: userID,

        itemId: String(itemId),
      });

      if (res?.data?.responseCode === "00") {
        return res.data.response || [];
      }

      return [];
    } catch (err) {
      console.error(err);

      return [];
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkItems.length) {
      toast.error("No items found");

      return { success: false, uploaded: 0 };
    }

    if (!userID) {
      toast.error("User ID missing");

      return { success: false, uploaded: 0 };
    }

    setBulkLoading(true);

    const clean = (val) => {
      if (val === null || val === undefined) return "";

      return String(val)
        .replace(/^"+|"+$/g, "")

        .trim();
    };

    const preparedItems = bulkItems

      .map((item) => ({
        itemName: clean(item.itemName),

        itemId: "",

        status: clean(item.status || "Y"),

        itemDescription: clean(item.itemDescription),

        category: clean(item.category),

        catId: "",

        price: clean(item.price),

        discount: clean(item.discount || "0"),

        discountPrice: clean(item.discountPrice || item.price),

        home_delivery_price: clean(item.home_delivery_price || item.price),

        takeaway_price: clean(item.takeaway_price || item.price),

        dining_price: clean(item.dining_price || item.price),

        room_order_price: clean(item.room_order_price || item.price),

        car_order_price: clean(item.car_order_price || item.price),

        expiryDate: clean(item.expiryDate),

        rackNumber: clean(item.rackNumber),

        // Half / Full / Regular must be sent here.

        variantName: clean(item.variantName || "Default"),

        variantId: clean(item.variantId),

        photo: clean(item.photo),

        iconDish: clean(item.iconDish || "Y"),

        ratings: clean(item.ratings || "0"),

        barcodeNumber: clean(item.barcodeNumber),

        customizations: clean(item.customizations || "[]"),

        categoryDescription: "",

        dish: "",

        itemNameTrans: "",

        itemDescriptionTrans: "",

        position: "",

        sizeTrans: "",

        subCategory: clean(item.subCategory),
      }))

      .filter((item) => item.itemName && item.price);

    if (!preparedItems.length) {
      toast.error("No valid items found");

      setBulkLoading(false);

      return { success: false, uploaded: 0 };
    }

    // Keep all variants of the same item together while creating chunks.

    // The uploaded CSV has 105 valid rows; sending everything in one request

    // can produce an empty response or server timeout.

    const groupedItems = new Map();

    preparedItems.forEach((item) => {
      const groupKey = `${item.category.toLowerCase()}__${item.itemName.toLowerCase()}`;

      if (!groupedItems.has(groupKey)) {
        groupedItems.set(groupKey, []);
      }

      groupedItems.get(groupKey).push(item);
    });

    const CHUNK_SIZE = 50;

    const chunks = [];

    let currentChunk = [];

    groupedItems.forEach((group) => {
      if (
        currentChunk.length > 0 &&
        currentChunk.length + group.length > CHUNK_SIZE
      ) {
        chunks.push(currentChunk);

        currentChunk = [];
      }

      currentChunk.push(...group);
    });

    if (currentChunk.length) {
      chunks.push(currentChunk);
    }

    let uploaded = 0;

    const responses = [];

    try {
      for (let index = 0; index < chunks.length; index += 1) {
        const chunk = chunks[index];

        const payload = {
          userid: String(userID),

          items: chunk,
        };

        const res = await api.post("/Order/v1.0/insertCatalogItem", payload, {
          timeout: 120000,
        });

        console.log(`Bulk upload chunk ${index + 1}/${chunks.length}`, {
          requestItems: chunk.length,

          status: res?.status,

          response: res?.data,
        });

        const responseCode = String(res?.data?.responseCode || "");

        const isEmptyObject =
          res?.data &&
          typeof res.data === "object" &&
          !Array.isArray(res.data) &&
          Object.keys(res.data).length === 0;

        const hasEmptyBody =
          res?.data === "" || res?.data === null || res?.data === undefined;

        const isHttpSuccess = res?.status >= 200 && res?.status < 300;

        // Some versions of this API return HTTP 2xx with an empty body.

        const isSuccess =
          responseCode === "00" ||
          (isHttpSuccess && (hasEmptyBody || isEmptyObject));

        if (!isSuccess) {
          const message =
            res?.data?.responseMessage ||
            res?.data?.message ||
            `Chunk ${index + 1} upload failed`;

          throw new Error(message);
        }

        uploaded += chunk.length;

        responses.push(res?.data ?? null);
      }

      // toast.success(`${uploaded} catalogue rows uploaded successfully`);

      setBulkItems([]);

      return {
        success: true,

        uploaded,

        totalChunks: chunks.length,

        responses,
      };
    } catch (err) {
      const serverData = err?.response?.data;

      console.error("Bulk catalogue upload error:", {
        message: err?.message,

        status: err?.response?.status,

        serverData,

        uploadedBeforeFailure: uploaded,
      });

      toast.error(
        serverData?.responseMessage ||
          serverData?.message ||
          err?.message ||
          `Upload stopped after ${uploaded} rows`,
      );

      return {
        success: false,

        uploaded,

        error: serverData || err?.message,
      };
    } finally {
      setBulkLoading(false);
    }
  };

  const addItemVariant = async ({ itemId, price, variation }) => {
    try {
      const res = await call("/Order/v1.0/InsertCatalogItemVariant", {
        userid: userID,

        itemId: String(itemId),

        price: String(price),

        variation: String(variation),

        variationId: "",
      });

      if (res?.data?.responseCode === "00") {
        toast.success(res.data.responseMessage || "Variant added");

        return true;
      } else {
        toast.error(res.data.responseMessage || "Failed to add variant");

        return false;
      }
    } catch (err) {
      console.error(err);

      toast.error("API error");

      return false;
    }
  };

  const enableDisablePackagingCharges = async (payload) => {
    try {
      const res = await call("/Order/EnableDisablePackagingCharges", payload);

      if (res?.data?.responseCode === "00") {
        return true;
      }

      toast.error(res?.data?.responseMessage || "Failed");

      return false;
    } catch (err) {
      console.error(err);

      toast.error("Error updating packaging charge");

      return false;
    }
  };

  const insertCatalogItem = async (payload) => {
    try {
      const res = await call("/Order/v1.0/insertCatalogItem", payload);

      if (res?.data?.responseCode === "00") {
        toast.success("Item added successfully");

        return true;
      } else {
        toast.error(res?.data?.responseMessage || "Failed");

        return false;
      }
    } catch (err) {
      console.error(err);

      toast.error("Error saving item");

      return false;
    }
  };

  return {
    items,

    loading,

    bulkItems,

    bulkLoading,

    hasNextPage,

    inventoryPage,

    INVENTORY_LIMIT,

    fetchInventory,

    getItemDetails,

    getCategories,

    getSubCategories,

    updateAllBarcodes,

    addBarcodeToItem,

    updateInventoryItem,

    uploadItemImage,

    updateCatalogItem,

    getFoodCustomizations,

    insertFoodCustomizations,

    getRecipeIngredients,

    setBulkItems,

    handleBulkSubmit,

    addItemVariant,

    enableDisablePackagingCharges,

    insertCatalogItem,
  };
}